import React, {Component} from 'react';
import {RouteComponentProps} from 'react-router';
import DashboardLayout, {DashboardTab} from 'layouts/Dashboard';
import {Button, Paper} from '@material-ui/core';
import {parseQueryString} from 'util/url';
import CreateDesignModal from '../components/CreateDesignModal';
import {Design, DesignField, DesignScreenGroupSyncStatus} from 'services/entities/designs';
import ProgressSpinner from 'components/ProgressSpinner';
import DesignListItem from 'pages/designs/components/DesignListItem';
import {deleteDesign, loadDesigns} from 'services/designs';
import ConfirmModal from 'components/ConfirmModal';
import {CancellablePromise, WrappedCancellablePromise} from 'util/promises';
import {logPageView} from 'services/analytics';
import config from 'config';
import RouteURLs from 'RouteURLs';
import {handleError} from 'util/errors';
import './index.scss';

interface DesignListPageState {
  newDesign: boolean;
  deletingDesign: string|null;
  designs: Design[]|null;
}

export default class DesignListPage extends Component<RouteComponentProps, DesignListPageState> {
  private loadDesignsPromise: CancellablePromise<Design[]>|null = null;
  private loadDesignsTimeout: any = null;

  state: DesignListPageState = {
    newDesign: false,
    deletingDesign: null,
    designs: null
  };

  componentDidMount(): void {
    logPageView(RouteURLs.DESIGN_LIST, 'Designs');
    const queryParams = parseQueryString(this.props.location.search);
    if('new' in queryParams) {
      this.setState({'newDesign': true});
    }
    this.loadDesigns();
  }

  componentWillUnmount(): void {
    this.cancelLoadingDesigns();
  }

  loadDesigns() {
    this.cancelLoadingDesigns();
    const fields = [
      DesignField.Space,
      DesignField.SpaceRegions,
      DesignField.DesignScreenGroups,
      DesignField.ScreenGroups,
      DesignField.DesignElementSources,
      DesignField.DesignThumbnailElements
    ];
    this.loadDesignsPromise = new WrappedCancellablePromise(loadDesigns({}, fields));
    this.loadDesignsPromise.then(
      designs => {
        this.loadDesignsPromise = null;
        this.setState({'designs': designs});
        this.reloadDesigns();
      },
      ex => {
        this.loadDesignsPromise = null;
        handleError('Failed to load designs', ex);
      }
    )
  }

  reloadDesigns() {
    if(this.loadDesignsTimeout) {
      return;
    }
    const designs = this.state.designs;
    if(designs && DesignListPage.hasSyncingScreenGroup(designs)) {
      this.loadDesignsTimeout = setTimeout(() => this.loadDesigns(), config.designs.reloadStatusDelay);
    }
  }

  cancelLoadingDesigns() {
    if(this.loadDesignsTimeout) {
      clearTimeout(this.loadDesignsTimeout);
      this.loadDesignsTimeout = null;
    }
    if(this.loadDesignsPromise) {
      this.loadDesignsPromise.cancel();
      this.loadDesignsPromise = null;
    }
  }

  static hasSyncingScreenGroup(designs: Design[]): boolean {
    return !!designs.find(des => !!des.screenGroups.find(
      sg => sg.syncStatus.thumbnail !== DesignScreenGroupSyncStatus.SYNCED));
  }

  onNewDesignClick = () => {
    this.setState({'newDesign': true});
  };

  onNewDesignClosing = () => {
    this.setState({'newDesign': false});
  };

  onNewDesignCreated = (design: Design) => {
    this.setState({'newDesign': false});
    this.loadDesigns();
  };

  onDeleteDesign = (design: Design) => {
    this.setState({'deletingDesign': design.code});
  };

  onDesignUpdated = (design: Design) => {
    this.loadDesigns();
  };

  onConfirmDeleteDesign = () => {
    const designCode = this.state.deletingDesign;
    const designs: Design[]|null = this.state.designs;
    if(!designCode || !designs) {
      return;
    }
    this.cancelLoadingDesigns();
    const newDesigns = designs!.filter((des) => des.code !== designCode);
    this.setState({'deletingDesign': null, 'designs': newDesigns});
    deleteDesign(designCode).then(
      () => this.loadDesigns(),
      err => handleError(err)
    );
  };

  onCancelDeleteDesign = () => {
    this.setState({'deletingDesign': null});
  };

  render() {
    const designs: Design[] = this.state.designs || [];
    const designsLoaded = this.state.designs !== null;
    const hasDesigns = designs.length > 0;
    return <DashboardLayout id="design-list-page" currentTab={DashboardTab.Designs}>
      <Button className="new-design" onClick={this.onNewDesignClick}>Create a design</Button>
      <h2>Your Designs</h2>
      <p>Click a screen to edit your design</p>
      { !hasDesigns &&
      <Paper className={'list-container' + (!hasDesigns ? ' empty' : '')}>
        { designsLoaded && <Button onClick={this.onNewDesignClick}>Create your first design</Button> }
        { !designsLoaded && <ProgressSpinner/> }
      </Paper> }
      { hasDesigns &&
      (designs as Design[]).map(des => <DesignListItem key={des.code} design={des} onDelete={this.onDeleteDesign}
                                                       onUpdated={this.onDesignUpdated} />) }
      <CreateDesignModal open={this.state.newDesign}
                         onClosing={this.onNewDesignClosing} onCreated={this.onNewDesignCreated} />
      <ConfirmModal open={!!this.state.deletingDesign} onConfirm={this.onConfirmDeleteDesign}
                   onCancel={this.onCancelDeleteDesign} title='Delete design'>
        <p>Are you sure you want to delete this design?</p>
      </ConfirmModal>
    </DashboardLayout>;
  }
}
